export const normalizeMultilineText = (
     text?: string,
     fallback = "No remarks"
    ) => {
     const cleaned = text?.split(/\r?\n/).filter(line => line.trim()).join("\n");
     return cleaned || fallback;
    };

/**
 * Splits `text` (result) into segments for highlighting against `rawQuery` (search).
 * Matches search characters in order against the result (case-insensitive). Extra characters
 * in the result (e.g. "1" in CN1 vs CNAA) are skipped without consuming search characters.
 */
export function splitTextForSearchHighlighting(
  text: string,
  rawQuery: string,
  highlightSnippet?: string,
): Array<{ text: string; highlight: boolean }> {
  // Primary path: use Typesense's highlight snippet
  if (highlightSnippet) {
    const unescaped = highlightSnippet
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&#x60;/g, "`");
    const parts = unescaped.split(/(<mark>.*?<\/mark>)/g);
    return parts
      .filter(p => p !== "")
      .map(p => {
        const isHighlight = p.startsWith('<mark>');
        return {
          text: isHighlight ? p.replace(/<\/?mark>/g, '') : p,
          highlight: isHighlight,
        };
      });
  }

  // Fallback: no snippet available, return text unhighlighted
  return [{ text: text ?? "", highlight: false }];
}

/**
 * Normalize for part-number style matching: ignore spaces, hyphens, slashes, underscores.
 * Prefix / suffix / substring on the raw part number are all satisfied by compact `pn.includes(q)`.
 */
export function normalizePartSearchKey(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, "");
}

/**
 * True → no mismatch modal.
 * Part number: compare normalized strings so `09103`, `3m080`, `8013` all match `09103M08013`.
 * Part name: normalized substring, plus plain lowercase substring for spaced phrases.
 * Empty search → compatible.
 */
export function isPartNumberCompatibleWithSearch(
  rawQuery: string,
  partNumber: string,
  partName?: string,
): boolean {
  const qRaw = rawQuery.trim();
  if (!qRaw) return true;
  const q = normalizePartSearchKey(qRaw);
  if (!q) return true;

  const pnCompact = normalizePartSearchKey(String(partNumber ?? ""));
  const nameRaw = String(partName ?? "").trim().toLowerCase();
  const nameCompact = normalizePartSearchKey(String(partName ?? ""));

  if (!pnCompact && !nameRaw && !nameCompact) return false;

  if (pnCompact.includes(q)) return true;
  if (nameCompact.includes(q)) return true;
  if (nameRaw.includes(qRaw.toLowerCase())) return true;
  return false;
}